import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { MatDialog } from '@angular/material/dialog';
import { PhotoPreviewComponent } from '../photo-preview/photo-preview.component';
import { stackNgbModalAboveCurrentOverlays } from '../utils/overlay-stacking.util';

@Component({
  selector: 'app-face-registration-input',
  templateUrl: './face-registration-input.component.html',
  styleUrls: ['./face-registration-input.component.scss']
})
export class FaceRegistrationInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() label: string = 'usersModule.biometric.title';
  @Input() buttonLabel: string = 'usersModule.biometric.register';
  @Input() disabled: boolean = false;
  @Input() previewUrl?: string | null;
  @Input() recognitionModalOptions?: NgbModalOptions;
  @Output() imageCaptured = new EventEmitter<{ bestImage: string; descriptor: number[] }>();

  stepUploadFile: boolean = true;
  isUploadError: boolean = false;
  progress: number = 100;
  fileName: string = '';
  fileSizeInKB: number = 0;
  registrationDate: Date = new Date();
  formStub: FormGroup;
  
  @ViewChild('faceRecognitionModal', { static: true }) faceRecognitionModal: ElementRef;
  modalRef: NgbModalRef | null = null;

  constructor(
    private fb: FormBuilder,
    private modalService: NgbModal,
    private dialog: MatDialog
  ) {
    this.formStub = this.fb.group({ personInvitationType: [null] });
  }

  ngOnInit(): void {
    if (this.previewUrl) {
      this.stepUploadFile = false;
      this.fileName = 'table.image';
      this.fileSizeInKB = this.calculateBase64SizeKB(this.previewUrl);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['previewUrl'] && changes['previewUrl'].currentValue) {
      this.stepUploadFile = false;
      this.fileName = 'table.image';
      this.fileSizeInKB = this.calculateBase64SizeKB(changes['previewUrl'].currentValue);
    }
  }

  openRecognition(): void {
    if (this.disabled) return;

    this.modalRef = this.modalService.open(this.faceRecognitionModal, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'lg',
      ...this.recognitionModalOptions,
      container: 'body',
      // Keep these after the spread so callers cannot drop the stacking classes (VDS-3114).
      windowClass: ['detail-access-modal', this.recognitionModalOptions?.windowClass]
        .filter(Boolean)
        .join(' '),
      backdropClass: ['blur-backdrop', 'face-recognition-backdrop', this.recognitionModalOptions?.backdropClass]
        .filter(Boolean)
        .join(' '),
    });
    this.modalRef.result.finally(() => {
      this.modalRef = null;
    });

    setTimeout(() => {
      this.forceModalZIndex();
    }, 100);

    this.modalRef.shown.subscribe(() => {
      this.forceModalZIndex();
    });
  }

  ngOnDestroy(): void {
    this._closeFaceModalIfOpen('component-destroy');
  }

  private _closeFaceModalIfOpen(reason: string): void {
    if (!this.modalRef) return;
    try {
      this.modalRef.dismiss(reason);
    } catch {
      /* already closed */
    }
    this.modalRef = null;
  }

  onFaceUrl(url: string) {
    this.previewUrl = url;
    this.stepUploadFile = false;
    this.isUploadError = false;
    this.fileName = 'face-capture.jpg';
    this.fileSizeInKB = this.calculateBase64SizeKB(url);
    this.registrationDate = new Date();
  }

  onFaceData(event: { bestImageBlob: Blob; bestImage: string; bestDescriptor: Float32Array }) {
    const serialized = Array.from(event.bestDescriptor ?? new Float32Array());
    this.previewUrl = event.bestImage;
    this.stepUploadFile = false;
    this.isUploadError = false;
    this.fileName = 'face-capture.jpg';
    this.fileSizeInKB = this.calculateBase64SizeKB(event.bestImage);
    this.registrationDate = new Date();
    this.imageCaptured.emit({ bestImage: event.bestImage, descriptor: serialized });
  }

  onCloseFaceModal() {
    this._closeFaceModalIfOpen('user-close');
  }

  openPhotoPreview(): void {
    if (!this.previewUrl) return;
    
    this.dialog.open(PhotoPreviewComponent, {
      data: {
        imageUrl: this.previewUrl,
        alt: 'Vista previa del registro facial'
      },
      panelClass: 'photo-preview-dialog',
      disableClose: false,
      autoFocus: false
    });
  }

  onClear() {
    this.previewUrl = null;
    this.stepUploadFile = true;
    this.isUploadError = false;
    this.fileName = '';
    this.fileSizeInKB = 0;
    this.imageCaptured.emit({ bestImage: '', descriptor: [] });
  }

  private calculateBase64SizeKB(base64: string): number {
    const str = base64.split(',')[1] || base64;
    const padding = (str.endsWith('==') ? 2 : str.endsWith('=') ? 1 : 0);
    const bytes = (str.length * 3) / 4 - padding;
    return Math.round(bytes / 1024);
  }

  /**
   * Place this Ngb window above the current top overlay (e.g. MatDialog user create).
   * Uses `!important` so it wins over leaked `::ng-deep .detail-access-modal { z-index: 999 }`.
   */
  private forceModalZIndex(): void {
    const windows = document.querySelectorAll('ngb-modal-window.detail-access-modal');
    const backdrops = document.querySelectorAll('.modal-backdrop.face-recognition-backdrop');
    const modalElement = windows.length ? (windows[windows.length - 1] as HTMLElement) : null;
    const backdropElement = backdrops.length ? (backdrops[backdrops.length - 1] as HTMLElement) : null;
    stackNgbModalAboveCurrentOverlays(modalElement, backdropElement);
  }

}


